"use client"
import React, { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getPlatformName } from "@/lib/utils/openlist"
import { Separator } from "@/components/ui/separator"
import { Download, FileText, ExternalLink, Copy } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import Link from "next/link"

import { Subject } from "@/types/subject"
import { usePermissions } from "@/hooks/use-permissions"
import { cn } from "@/lib/utils"

function SubjectDownloadPanelCom({ subject }: { subject: Subject }) {
  const downloadLinks = subject.downloadLinks || []
  const primaryDownloadLink = downloadLinks.find(link => link.isPrimary) || downloadLinks[0]
  const backupDownloadLinks = downloadLinks.filter(link => !link.isPrimary)
  const { hasPermission, isReady } = usePermissions()

  // 使用状态来避免水合不匹配
  const [hasDownloadPermission, setHasDownloadPermission] = useState(false)
  const [isPermissionReady, setIsPermissionReady] = useState(false)

  useEffect(() => {
    if (isReady) {
      const permission = hasPermission("subject:read:netdisk", {
        adminPass: false,
      })
      setHasDownloadPermission(permission)
      setIsPermissionReady(true)
    }
  }, [isReady, hasPermission])
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          资源下载
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {primaryDownloadLink ? (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {getPlatformName(primaryDownloadLink.platform)}
                </span>
                <Badge variant="secondary">推荐</Badge>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {subject.name} 完整资料包
                  </span>
                </div>
                <div className="text-xs text-gray-500">{primaryDownloadLink.description}</div>
                {primaryDownloadLink.fileSize && (
                  <div className="text-xs text-gray-400">
                    文件大小: {primaryDownloadLink.fileSize}
                  </div>
                )}
              </div>

              <Button
                asChild={isPermissionReady && hasDownloadPermission}
                className={cn(
                  !isPermissionReady || !hasDownloadPermission ? "cursor-not-allowed" : "",
                  "w-full"
                )}
                variant="outline"
                disabled={!isPermissionReady}
              >
                {isPermissionReady && hasDownloadPermission ? (
                  <Link href={primaryDownloadLink.url} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    获取下载链接
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Link>
                ) : (
                  <span>{!isPermissionReady ? "加载中..." : "VIP用户可获取网盘地址"}</span>
                )}
              </Button>

              {primaryDownloadLink.password && (
                <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-700 dark:text-blue-300">
                      提取码:
                      {isPermissionReady && hasDownloadPermission
                        ? primaryDownloadLink.password
                        : !isPermissionReady
                          ? "加载中..."
                          : "VIP用户可获取"}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!isPermissionReady || !hasDownloadPermission}
                      onClick={() => {
                        if (!hasDownloadPermission || !isPermissionReady) return
                        navigator.clipboard.writeText(primaryDownloadLink.password!)
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {backupDownloadLinks.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    备用下载
                  </div>
                  {backupDownloadLinks.map(link => (
                    <div key={link.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{getPlatformName(link.platform)}</span>
                        <Badge variant="outline">备用</Badge>
                      </div>
                      <Button
                        asChild={isPermissionReady && hasDownloadPermission}
                        className="w-full"
                        variant="ghost"
                        size="sm"
                        disabled={!isPermissionReady}
                      >
                        {isPermissionReady && hasDownloadPermission ? (
                          <Link href={link.url} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4 mr-2" />
                            {getPlatformName(link.platform)}下载
                            <ExternalLink className="h-4 w-4 ml-2" />
                          </Link>
                        ) : (
                          <span>
                            <Download className="h-4 w-4 mr-2" />
                            {!isPermissionReady ? "加载中..." : "VIP用户可获取网盘地址"}
                            <ExternalLink className="h-4 w-4 ml-2" />
                          </span>
                        )}
                      </Button>
                      {link.password && (
                        <div className="text-xs text-gray-500 text-center">
                          提取码:{" "}
                          {isPermissionReady && hasDownloadPermission
                            ? link.password
                            : !isPermissionReady
                              ? "加载中..."
                              : "VIP用户可获取"}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-500 dark:text-gray-400">暂无下载链接</div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default SubjectDownloadPanelCom
